import React from "react";
import { AlertTriangle, Home, RefreshCw } from "lucide-react";

/**
 * Global error boundary. Wraps children and renders a friendly fallback UI
 * if any child throws during render. Logs the error to console so it shows
 * up in browser devtools and the structured access middleware on the server.
 *
 * Usage:
 *   <ErrorBoundary>
 *     <App />
 *   </ErrorBoundary>
 *
 * Reset on URL change is handled implicitly because React Router unmounts
 * the route's children on navigation.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // eslint-disable-next-line no-console
    console.error("ErrorBoundary caught:", error, errorInfo);
    this.setState({ errorInfo });
  }

  componentDidUpdate(prevProps) {
    // Auto-reset when the consumer-provided resetKey changes (e.g. URL change).
    // This lets users navigate away from a broken page without manual retry.
    if (
      this.state.hasError &&
      prevProps.resetKey !== this.props.resetKey
    ) {
      this.setState({ hasError: false, error: null, errorInfo: null });
    }
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div
        className="min-h-screen flex items-center justify-center px-6"
        style={{ background: "var(--bg-primary, #0b0811)", color: "var(--text-primary, #fff)" }}
        data-testid="error-boundary"
      >
        <div className="max-w-lg w-full">
          <div className="w-14 h-14 rounded-2xl bg-red-500/15 text-red-400 flex items-center justify-center mb-5">
            <AlertTriangle className="w-7 h-7" />
          </div>
          <h1
            className="text-3xl font-black tracking-tight mb-2"
            data-testid="error-boundary-title"
          >
            Une erreur est survenue
          </h1>
          <p className="text-zinc-400 mb-4 text-sm">
            La page que vous consultez a rencontré un problème. L'équipe a été notifiée.
          </p>
          {this.state.error?.message && (
            <pre
              className="text-xs bg-zinc-900/60 border border-zinc-800 rounded-lg p-3 mb-5 overflow-auto max-h-40 text-red-300"
              data-testid="error-boundary-message"
            >
              {String(this.state.error.message)}
            </pre>
          )}
          <div className="flex gap-3">
            <button
              onClick={this.handleReset}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#f7c935] text-[#14101c] font-bold hover:bg-[#e6b825] transition-colors"
              data-testid="error-boundary-retry-btn"
            >
              <RefreshCw className="w-4 h-4" /> Réessayer
            </button>
            <a
              href="/dashboard"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-white/10 text-white hover:bg-white/5 transition-colors"
              data-testid="error-boundary-home-btn"
            >
              <Home className="w-4 h-4" /> Accueil
            </a>
          </div>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
